import React, { useEffect, useState } from "react";

import ShipeeziNewGrid, { Column, GeneralActions } from "Components/AppGrid/shipeezi-new-grid";
import Modal from "Components/Generic/Modal";
import { Location } from "Models/location";
import { Member } from "Models/members/member";
import { Option } from "Models/option";
import { Filters, Searches } from "Models/shipeezi.table";
import { Roles } from "Models/user";
import { UserCompany } from "Models/user.company";
import { useTranslation } from "react-i18next";
import { UserTypes } from "Util/enums";
import { TypeOfMember } from "Util/enums/members";

import useEquipmentActions from "../../../../../hooks/useEquipmentActions";
import { Content } from "./styles";

export interface AddMemberModalProps {
  isOpened: boolean;
  onClose: () => void;
  membersIdAdded: Array<number>;
  onConfirmAddMembers: (members: Array<Member>) => void;
  rolesList?: Roles[];
  userTypes?: Option[];
}

export const AddMemberModal: React.FC<AddMemberModalProps> = ({
  isOpened,
  onClose,
  membersIdAdded,
  onConfirmAddMembers,
  rolesList,
  userTypes,
}) => {
  const { t: translate } = useTranslation();

  const [members, setMembers] = useState<Member[]>([]);
  const [selectedMembers, setSelectedMembers] = useState<Array<Member>>([]);

  const handleSelect = (idsSelected: number[]) => {
    const membersSelected = [...selectedMembers, ...members].filter((member) =>
      idsSelected.includes(member.id!)
    );

    setSelectedMembers(membersSelected);
  };

  const [page, setPage] = useState<number>(0);
  const setCurrentPage = (numberPage: number) => setPage(numberPage - 1);

  const [pageSize, setPageSize] = useState<number>(20);
  const [total, setTotal] = useState<number>(0);
  const [filters, setFilters] = useState<Filters>();
  const [searches, setSearches] = useState<Searches>([]);
  const { getAllMembers, countMembers } = useEquipmentActions();

  const { data: membersData, isFetching: isFetchingData } = getAllMembers(
    page,
    pageSize,
    searches,
    filters
  );
  const { data: membersCount, isFetching: isFetchingCount } = countMembers(filters, searches);

  const onCloseModal = () => {
    setSelectedMembers([]);

    setFilters(undefined);
    setSearches([]);
    onClose();
  };

  useEffect(() => {
    if (membersData) {
      setMembers(membersData);
    }
  }, [membersData]);

  useEffect(() => {
    if (membersCount) {
      setTotal(membersCount);
    }
  }, [membersCount]);

  const columns: Column[] = [
    {
      name: "userCompany",
      title: translate("code"),
      formatText: (userCompany?: UserCompany) => userCompany?.code || "-",
      hideSearch: true,
    },
    { name: "fullName", title: translate("name"), searchIdentifier: "full_name" },
    { name: "email", title: translate("email") },
    { name: "typeOfMember", title: translate("typeOfMember"), hideSearch: true },
    {
      name: "userCompany",
      title: translate("typeOfUser"),
      formatText: (userCompany?: UserCompany) => userCompany?.typeUser || "-",
      hideSearch: true,
    },
    {
      name: "userCompany",
      title: `${translate("role")}(${translate("security").toLowerCase()})`,
      formatText: (userCompany?: UserCompany) => userCompany?.role?.name || "-",
      hideSearch: true,
    },
    {
      name: "location",
      title: translate("location"),
      formatText: (location?: Location) =>
        location?.country || location?.state || location?.suburb || location?.addressLine1 || "-",
      hideSearch: true,
    },
  ];

  const handleSearch = (values: Searches) => {
    setSearches(values);
    setPage(0);
  };

  const handleFilter = (value: Filters) => {
    setFilters(value);
    setPage(0);
  };

  const generalActions: GeneralActions = {
    orderBy: {
      left: ["handleSearch", "handleFilterBy"],
    },
    handleSearch: {
      onSearch: (filters) => {
        handleSearch(filters);
      },
    },
    handleFilterBy: {
      categories: [
        {
          name: translate("typeOfMember"),
          column: "member.typeOfMember",
          options: [TypeOfMember.STAFF, TypeOfMember.USER],
        },
        {
          name: translate("typeOfUser"),
          column: "userCompany.typeUser",
          options:
            userTypes
              ?.filter((type) => type.name !== UserTypes.STAFF)
              ?.map((userType) => userType.name) || [],
        },
        {
          name: translate("role"),
          column: "role.name",
          options: rolesList?.map((role) => role.name) || [],
        },
      ],
      onChange: handleFilter,
    },
  };

  const onSubmit = () => {
    onConfirmAddMembers(selectedMembers);
    onCloseModal();
  };

  return (
    <div>
      <Modal
        open={isOpened}
        handleClose={onClose}
        title={translate("addAMember")}
        showActionButtons
        actionButtonProps={{
          onSave: onSubmit,
          onCancel: onCloseModal,
          textSave: translate("addMembers"),
        }}
      >
        <Content>
          <ShipeeziNewGrid
            data={members}
            totalItems={total}
            columns={columns}
            loadingData={isFetchingData || isFetchingCount}
            emptyData={translate("noMembersToShow")}
            selecteds={membersIdAdded}
            handleSelect={handleSelect}
            pageSize={pageSize}
            pageSizeList={[20, 40, 60, 100]}
            currentPage={page}
            onChangePageSize={setPageSize}
            onNextPage={setCurrentPage}
            onPreviousPage={setCurrentPage}
            onGoToPage={setCurrentPage}
            positionActionsColumn="right"
            generalActions={generalActions}
            showSort={false}
          />
        </Content>
      </Modal>
    </div>
  );
};
